import { adminActive, schedAction } from '../state/admin';
import { editSheet } from '../state/adminUi';
import { completions } from '../state/completions';
import { dayFilter, mineOnly, viewMode } from '../state/filters';
import type { CallView } from '../state/schedule';
import { boardState, week } from '../state/schedule';
import { techName } from '../state/session';
import { toast } from '../state/toast';
import { CallCard } from './CallCard';

interface BoardProps {
  onDone: (c: CallView) => void;
  onMissed: (c: CallView) => void;
  onRetry: () => void;
}

/** Swap a call with its neighbor in the day's SERVER order (reorderDay is order-only). */
async function moveInDay(weekId: string, dayKey: string, id: string, dir: -1 | 1): Promise<void> {
  const day = week.value?.days.find((d) => d.key === dayKey);
  if (!day) return;
  const ids = day.calls.map((c) => c.id);
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return;
  [ids[i], ids[j]] = [ids[j]!, ids[i]!];
  await schedAction({ action: 'reorderDay', weekOf: weekId, day: dayKey, ids });
}

async function deleteFromDay(weekId: string, c: CallView): Promise<void> {
  const who = (c.development ? `${c.development} — ` : '') + (c.address || 'this call');
  if (!confirm(`Delete ${who}? This cannot be undone.`)) return;
  const ok = await schedAction({ action: 'deleteCall', weekOf: weekId, id: c.id });
  if (ok) toast('Deleted.');
}

/** The daily-driver view: day sections with sticky band headers; resolved cards sink
 *  to the bottom of their day; tabs and chips filter in place (one board, v1 model). */
export function Board({ onDone, onMissed, onRetry }: BoardProps) {
  const w = week.value;
  const comps = completions.value;
  const mine = mineOnly.value && techName.value;

  if (boardState.value === 'loading' && !w) {
    return (
      <div class="space-y-3 p-3" aria-label="Loading schedule">
        {[0, 1, 2].map((i) => (
          <div key={i} class="h-28 animate-pulse rounded-xl bg-slate-200" />
        ))}
      </div>
    );
  }
  if (boardState.value === 'error' && !w) {
    return (
      <div class="flex flex-col items-center gap-3 p-8 text-center">
        <p class="font-semibold text-navy-900">Couldn't load the schedule.</p>
        <button class="btn-primary" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (!w) return null;

  const days = w.days
    .map((d) => {
      let calls = d.calls;
      if (mine) calls = calls.filter((c) => c.tech.toLowerCase() === techName.value.toLowerCase());
      if (viewMode.value === 'resolved') calls = calls.filter((c) => comps[c.id]);
      // Active first (original order), resolved sink to the bottom of their day.
      const active = calls.filter((c) => !comps[c.id]);
      const resolved = calls.filter((c) => comps[c.id]);
      return { ...d, calls: [...active, ...resolved] };
    })
    .filter((d) => dayFilter.value === 'all' || d.key === dayFilter.value);

  const total = days.reduce((n, d) => n + d.calls.length, 0);
  if (total === 0) {
    return (
      <div class="p-8 text-center text-slate-500">
        {viewMode.value === 'resolved' ? 'Nothing closed out yet this week.' : 'No calls here.'}
      </div>
    );
  }

  return (
    <div class="mx-auto max-w-3xl pb-24">
      {days.map(
        (d) =>
          d.calls.length > 0 && (
            <section key={d.key} aria-label={d.label || d.key}>
              <h2 class="sticky top-[104px] z-10 flex items-baseline gap-2 border-b border-band-edge bg-band px-3 py-2 text-sm font-bold text-white">
                {d.label || d.key}
                <span
                  class="text-xs font-semibold text-white/60"
                  aria-label={`${d.calls.length} calls`}
                >
                  {d.calls.length}
                </span>
                {d.date && isToday(d.date) && (
                  <span class="ml-auto rounded-full bg-brand-600 px-2 py-0.5 text-xs font-bold">
                    Today
                  </span>
                )}
                {adminActive.value && w.weekId && (
                  <button
                    class={`tap !min-h-0 rounded-full bg-white/10 px-2 py-0.5 text-xs font-bold ${d.date && isToday(d.date) ? '' : 'ml-auto'}`}
                    data-testid={`btn-add-call-${d.key}`}
                    onClick={() =>
                      (editSheet.value = {
                        mode: 'add',
                        call: { slot: '8–12' },
                        day: d.key,
                        weekOf: w.weekId,
                        fromDb: false,
                      })
                    }
                  >
                    ＋ Add
                  </button>
                )}
              </h2>
              <div class="space-y-2.5 p-3">
                {d.calls.map((c) => (
                  <CallCard
                    key={c.id}
                    call={c}
                    rec={comps[c.id] ?? null}
                    onDone={() => onDone(c)}
                    onMissed={() => onMissed(c)}
                    onReschedule={() => {}}
                    admin={
                      adminActive.value && w.weekId
                        ? {
                            weekOf: w.weekId,
                            day: d.key,
                            onDelete: () => void deleteFromDay(w.weekId, c),
                            onMove: (dir) => void moveInDay(w.weekId, d.key, c.id, dir),
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </section>
          ),
      )}
    </div>
  );
}

function isToday(iso: string): boolean {
  const now = new Date();
  const local = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return iso === local;
}
